import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Sparkles } from "lucide-react";
import { useJobberSyncStatus } from "../hooks/useJobberSyncStatus";
import WaitlistMessagingPreferences from "../components/waitlist/WaitlistMessagingPreferences";
import WaitlistCalendarOpenings from "../components/waitlist/WaitlistCalendarOpenings";
import WaitlistClientsTable from "../components/waitlist/WaitlistClientsTable";
import TextMessagesTable from "../components/waitlist/TextMessagesTable";
import AvailabilityNew from "./AvailabilityNew";

const staticTotals = [];
const TEXTING_HOUR_START = 8;
const TEXTING_HOUR_END = 19;

const localTimeParts = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "numeric",
      hourCycle: "h23",
    }).formatToParts(date);

    return {
      hour: Number(parts.find((part) => part.type === "hour")?.value),
      minute: Number(parts.find((part) => part.type === "minute")?.value),
    };
  } catch {
    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
};

export default function Waitlist() {
  const { status, loading: statusLoading } = useJobberSyncStatus();
  const toast = useToast();
  const [now, setNow] = useState(() => new Date());
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [copy, setCopy] = useState({});
  const [products, setProducts] = useState([]);
  const settingsRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [editExisting, setEditExisting] = useState(0);
  const [editOpenSlot, setEditOpenSlot] = useState(0);
  const [editFrequency, setEditFrequency] = useState(0);
  const [editEnabledProductIds, setEditEnabledProductIds] = useState(new Set());

  useEffect(() => {
    fetchPreferences();
    fetchStats();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPreferences = async () => {
    setPreferencesLoading(true);
    try {
      const response = await fetch("/api/waitlist_preferences");
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load waitlist preferences");
      }
      setCopy(data.copy || {});
      setProducts(data.products || []);
      if (data.preferences) {
        setEditExisting(data.preferences.waitlist_existing_appt_days_threshold ?? 0);
        setEditOpenSlot(data.preferences.waitlist_open_slot_days_threshold ?? 0);
        setEditFrequency(data.preferences.waitlist_text_frequency_days ?? 0);
      }
      setEditEnabledProductIds(
        new Set((data.products || []).filter((p) => p.waitlist_text_enabled).map((p) => p.id))
      );
    } catch (error) {
      setCopy({});
      setProducts([]);
    } finally {
      setPreferencesLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch("/api/waitlist_stats");
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load waitlist stats");
      }
      setStats(data.stats);
    } catch (error) {
      setStats(null);
    }
  };

  const handleToggleProduct = (productId) => {
    setEditEnabledProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleSavePreferences = async () => {
    setPreferencesSaving(true);
    try {
      const response = await fetch("/api/waitlist_preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name=\"csrf-token\"]').content,
        },
        body: JSON.stringify({
          preferences: {
            waitlist_existing_appt_days_threshold: editExisting,
            waitlist_open_slot_days_threshold: editOpenSlot,
            waitlist_text_frequency_days: editFrequency,
          },
          enabled_product_ids: Array.from(editEnabledProductIds),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.errors?.join(", ") || "Failed to save settings");
      }

      toast({
        title: "Preferences saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchPreferences();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPreferencesSaving(false);
    }
  };

  const hasBusiness = !!status?.business?.external_account_id;
  const connectedProvider = status?.connected_provider || null;
  const providerConnected = !!status?.provider_connected;
  const providerLabel = connectedProvider === "service_m8" ? "ServiceM8" : "Jobber";
  const waitlistSignalLabel = connectedProvider === "service_m8" ? "Same Day Openings waitlist setting" : "Jobber waitlist custom field";
  const reconnectHref = connectedProvider === "service_m8" ? "/oauth/service_m8/start" : "/jobber/oauth/authorize";
  const needsReconnect = !!status?.needs_reauth || (hasBusiness && status?.business?.connected === false);
  const syncComplete = status?.business?.sync_status === "completed";
  const syncPhase = status?.business?.sync_state_breakdown?.current_phase || null;
  const waitlistCustomersCount = stats?.waitlist_customers_count ?? stats?.waitlist_opt_ins ?? 0;
  const noWaitlistCustomersYet = stats != null && waitlistCustomersCount === 0;
  const customersReceiveCopy = copy.customers_receive || {};
  const textingTimezone = status?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  const textingLocalTime = localTimeParts(now, textingTimezone);
  const outsideTextingHours = textingLocalTime.hour < TEXTING_HOUR_START || textingLocalTime.hour >= TEXTING_HOUR_END;
  const resumesToday = textingLocalTime.hour < TEXTING_HOUR_START;

  return (
    <Box maxW="1800px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Flex align="center" mb={2}>
            <Sparkles size={32} color="#3182ce" style={{ marginRight: "12px" }} />
            <Heading size="lg">Waitlist Autopilot</Heading>
          </Flex>
          <Text color="gray.600" mt={1} fontSize="sm">
            Fill last-minute openings by automatically texting waitlist customers when earlier spots become available.
          </Text>
        </Box>

        {outsideTextingHours && (
          <Alert status="info" variant="left-accent" borderRadius="md" bg="blue.50" borderColor="blue.200">
            <AlertIcon />
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="blue.900">
                Waitlist texts are paused outside 8:00 AM-7:00 PM local time.
              </Text>
              <Text fontSize="sm" color="blue.800">
                To stay compliant with texting rules, automated customer texts will resume after 8:00 AM {resumesToday ? "today" : "tomorrow"} in {textingTimezone}.
              </Text>
            </Box>
          </Alert>
        )}

        <Tabs colorScheme="blue" variant="enclosed" defaultIndex={4}>
          <TabList overflowX="auto" overflowY="hidden" borderBottom="1px solid" borderColor="gray.200">
            <Tab flexShrink={0}>How It Works</Tab>
            <Tab flexShrink={0}>Who Gets Texts and When</Tab>
            <Tab flexShrink={0}>Worker Availability</Tab>
            <Tab flexShrink={0}>Waitlist Customers</Tab>
            <Tab flexShrink={0}>Openings Ready to Fill</Tab>
            <Tab flexShrink={0}>Waitlists Filled</Tab>
            <Tab flexShrink={0}>Text Messages</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={6}>
              <Box border="1px solid" borderColor="blue.200" borderRadius="xl" p={{ base: 5, md: 6 }} bg="blue.50">
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  <VStack align="stretch" spacing={5}>
                    <Box>
                      <Heading size="sm" color="blue.900" mb={2}>
                        How it works
                      </Heading>
                      <Text fontSize="sm" color="blue.800">
                        Your team asks during booking whether the customer wants to hear about an earlier opening. If the customer says yes, your team marks the {waitlistSignalLabel}, and Same Day Openings handles the follow-up text flow from there.
                      </Text>
                    </Box>

                    {noWaitlistCustomersYet && (
                      <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                        <Text fontSize="sm" color="blue.900" fontWeight="semibold">
                          Your waitlist is ready, but no customers have been added yet.
                        </Text>
                        <Text fontSize="sm" color="blue.800" mt={1}>
                          Once your team starts marking the {waitlistSignalLabel} for customers who say yes, matches and texts will appear here.
                        </Text>
                      </Box>
                    )}

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          What your team does
                        </Text>
                        <Text fontSize="sm" color="gray.800">
                          Ask on the booking call and only mark the {waitlistSignalLabel} when the customer says yes.
                        </Text>
                      </Box>
                      <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          What Same Day Openings does
                        </Text>
                        <Text fontSize="sm" color="gray.800">
                          We use that custom field as the signal to send confirmation and opening texts from your waitlist number.
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={3}>
                      {[
                        "1. Your team adds customers to the waitlist",
                        "2. An opening appears in your schedule",
                        "3. We text matching customers, or you call them if they have not opted in to automatic texting",
                        "4. We automate the fill",
                      ].map((step) => (
                        <Box key={step} border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                          <Text fontSize="sm" color="gray.800" fontWeight="medium">
                            {step}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>

                  </VStack>

                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Heading size="sm" color="blue.900" mb={2}>
                        {customersReceiveCopy.title || "What your customers receive"}
                      </Heading>
                      <Text fontSize="sm" color="blue.800">
                        {customersReceiveCopy.description || "Customers you add to the waitlist receive a confirmation text and, when there is a match, an offer for an earlier opening."}
                      </Text>
                    </Box>

                    <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {customersReceiveCopy.confirmation_label || "Waitlist confirmation"}
                      </Text>
                      <Text fontSize="sm" color="gray.800">
                        {customersReceiveCopy.confirmation_preview || "\"Preview unavailable.\""}
                      </Text>
                    </Box>

                    <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {customersReceiveCopy.offer_label || "Open slot offer"}
                      </Text>
                      <Text fontSize="sm" color="gray.800">
                        {customersReceiveCopy.offer_preview || "\"Preview unavailable.\""}
                      </Text>
                    </Box>

                    <Box border="1px solid" borderColor="blue.100" borderRadius="lg" p={4} bg="white">
                      <Heading size="xs" textTransform="uppercase" color="blue.700" letterSpacing="wide" mb={2}>
                        Current status
                      </Heading>
                      {statusLoading ? (
                        <HStack justify="center" py={4}>
                          <Spinner size="sm" color="blue.500" />
                        </HStack>
                      ) : (
                        <VStack align="stretch" spacing={3}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme={providerConnected ? "green" : "red"}>
                              {providerConnected ? `${providerLabel} connected` : (needsReconnect ? "Reconnect required" : "Provider not connected")}
                            </Badge>
                            {status?.business?.sync_status && (
                              <Badge colorScheme="blue" variant="subtle">
                                Sync: {status.business.sync_status}
                              </Badge>
                            )}
                          </HStack>
                          {status?.business?.sync_state_breakdown?.current_phase && (
                            <Text fontSize="xs" color="gray.500">
                              {status.business.sync_state_breakdown.current_phase}
                            </Text>
                          )}
                          {needsReconnect && (
                            <Button as="a" href={reconnectHref} size="sm" colorScheme="red" alignSelf="start">
                              Reconnect {providerLabel}
                            </Button>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            These message templates are fixed to protect deliverability and reduce spam risk.
                          </Text>
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </SimpleGrid>
              </Box>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <Box ref={settingsRef} border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} bg="white">
                <Box>
                  <Heading size="sm" mb={1}>Who gets texts and when</Heading>
                  <Text mt={1} fontSize="sm" color="gray.600">
                    Set the rules for who can receive waitlist texts, how often, and which services are eligible.
                  </Text>
                </Box>

                <WaitlistMessagingPreferences
                  loading={preferencesLoading}
                  daysAway={editExisting}
                  setDaysAway={setEditExisting}
                  openSlotDaysAway={editOpenSlot}
                  setOpenSlotDaysAway={setEditOpenSlot}
                  frequencyDays={editFrequency}
                  setFrequencyDays={setEditFrequency}
                  products={products}
                  enabledProductIds={editEnabledProductIds}
                  onToggleProduct={handleToggleProduct}
                />

                <Button
                  mt={4}
                  size="sm"
                  variant="outline"
                  borderColor="gray.800"
                  color="gray.800"
                  onClick={handleSavePreferences}
                  isLoading={preferencesSaving}
                >
                  Save texting rules
                </Button>
              </Box>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <AvailabilityNew embedded />
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <WaitlistClientsTable />
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <VStack align="stretch" spacing={6}>
                <WaitlistCalendarOpenings
                  title="Openings ready to fill"
                  subtitle="Review upcoming openings and turn automation on for the ones you want us to text about."
                  syncComplete={syncComplete}
                  syncPhase={syncPhase}
                  noWaitlistCustomersYet={noWaitlistCustomersYet}
                  showGenerateButton={false}
                  providerLabel={providerLabel}
                  waitlistSignalLabel={waitlistSignalLabel}
                />

                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} bg="white">
                  <Heading size="sm" mb={1}>Recent results</Heading>
                  <Text mt={1} fontSize="sm" color="gray.600">
                    See invite volume, conversion, and automation performance across your waitlist.
                  </Text>
                  <Button
                    mt={3}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowStats((prev) => !prev)}
                  >
                    {showStats ? "Hide results" : "View results"}
                  </Button>

                  {showStats && (
                    <SimpleGrid mt={4} columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Invite texts sent</StatLabel>
                        <StatNumber fontSize="lg">{stats?.invite_texts_sent ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Waitlist customers</StatLabel>
                        <StatNumber fontSize="lg">{stats ? waitlistCustomersCount : "—"}</StatNumber>
                        {noWaitlistCustomersYet && (
                          <StatHelpText mb={0} fontSize="xs">
                            No customers have been opted in yet.
                          </StatHelpText>
                        )}
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">
                          Openings in next {stats?.window_days ?? 14} days
                        </StatLabel>
                        <StatNumber fontSize="lg">{stats?.openings_next_window ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">
                          Openings completed in next {stats?.window_days ?? 14} days
                        </StatLabel>
                        <StatNumber fontSize="lg">{stats?.openings_filled_next_window ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Waitlist automation on</StatLabel>
                        <StatNumber fontSize="lg">{stats?.automation_on ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Waitlist automation off</StatLabel>
                        <StatNumber fontSize="lg">{stats?.automation_off ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Total texted</StatLabel>
                        <StatNumber fontSize="lg">{stats?.total_texted ?? "—"}</StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">
                          Conversion rate per text (last {stats?.window_days ?? 14} days)
                        </StatLabel>
                        <StatNumber fontSize="lg">
                          {stats?.conversion_rate_per_text != null ? `${stats.conversion_rate_per_text}%` : "—"}
                        </StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Time to agreement</StatLabel>
                        <StatNumber fontSize="lg">
                          {stats?.time_to_agreement_days != null ? `${stats.time_to_agreement_days} days` : "—"}
                        </StatNumber>
                      </Stat>
                      <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <StatLabel fontSize="xs" color="gray.500">Opt-ins from invite</StatLabel>
                        <StatNumber fontSize="lg">
                          {stats?.opt_in_from_invite_rate != null ? `${stats.opt_in_from_invite_rate}%` : "—"}
                        </StatNumber>
                      </Stat>
                      {staticTotals.map((item) => (
                        <Stat key={item.label} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                          <StatLabel fontSize="xs" color="gray.500">{item.label}</StatLabel>
                          <StatNumber fontSize="lg">{item.value}</StatNumber>
                        </Stat>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              </VStack>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <WaitlistCalendarOpenings
                title="Waitlists filled"
                subtitle="Review openings that were successfully filled from the waitlist."
                syncComplete={syncComplete}
                syncPhase={syncPhase}
                noWaitlistCustomersYet={false}
                showGenerateButton={false}
                initialFilledFilter="on"
                includeFilledHistory
                showFilledAt
                readOnly
                emptyMessage="No waitlists have been filled yet."
              />
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <TextMessagesTable />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
}
